import { Injectable } from '@angular/core';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { App } from '@df/core/app';
import { Nexus } from '@tomandco/nexus-api-types';
import { Subject } from 'rxjs';

export interface IObserverParams {
    level: number;
    title?: string;
    args: any[];
    stack?: string;
}

export type IObserver = (params: IObserverParams) => void;

@Injectable({
    providedIn: 'root'
})
export class Prefs {
    /**
     * Customer local storage currency code
     */
    private get currencyCode(): string | undefined {
        return this.localStorageService.get('currencyCode');
    }
    private set currencyCode(value: string | undefined) {
        this.localStorageService.set('currencyCode', value);
    }
    /**
     * Current currency
     */
    private currency?: Nexus.Lib.General.Currency;

    /**
     * Base currency
     */
    baseCurrency!: Nexus.Lib.General.Currency;

    private initPromise: Promise<void>;

    /**
     * Observables
     */
    obs: {
        /**
         * Subscribe to this observble for currency changes
         */
        currency: Subject<Nexus.Lib.General.Currency>;
    } = <any>{};

    constructor(
        private app: App,
        private localStorageService: LocalStorageService,
        private apiConfigService: DfApiConfigService
    ) {
        this.app.prefs = this;

        this.obs.currency = new Subject<Nexus.Lib.General.Currency>();
        this.initPromise = new Promise(async resolve => {
            await this.apiConfigService.loadApiConfig();
            this.update();
            resolve();
        });
    }

    init(): Promise<void> | undefined {
        if (!this.currency) {
            return this.initPromise;
        }
        return undefined;
    }

    /**
     * Sets currency code prefered by customer
     */
    setCurrencyCode(code: string | undefined) {
        if (this.currencyCode !== code) {
            this.currencyCode = code;
            this.update();
            this.obs.currency.next(this.getCurrency());
        }
    }

    /**
     * Returns current currency code
     */
    getCurrencyCode(): string {
        return this.getCurrency().code;
    }

    /**
     * Returns currently active currency
     * @return {object}
     */
    getCurrency() {
        return this.currency!;
    }

    /**
     * Updates current currency
     */
    update(): void {
        let currency: Nexus.Lib.General.Currency | undefined;
        this.baseCurrency = this.app.data.currency.list.find(i => i.code === this.app.data.currency.base)!;
        if (this.currencyCode) {
            currency = this.app.data.currency.list.find(i => i.code === this.currencyCode);
            if (!currency) {
                this.currencyCode = undefined;
            }
        }
        if (!currency) {
            currency = this.baseCurrency;
        }

        this.currency = <Nexus.Lib.General.Currency>currency;
        if (!this.currency.rate && this.currency.code === this.app.data.currency.base) {
            this.currency.rate = 1;
        }
    }

    get currencySymbol(): string | undefined {
        const base = this.app.data?.currency?.base;

        const currency = this.app.data?.currency?.list?.find(curr => curr.code === base);
        if (currency) {
            return currency.symbol;
        }
        return undefined;
    }
}

declare module '@df/core/app' {
    interface App {
        prefs: Prefs;
    }
}
